import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check, ExternalLink, BarChart3, Code, X } from 'lucide-react';
import { analyticsService, AnalyticsConfig } from '../../lib/analytics';
import { useBusiness } from '../../contexts/BusinessContext';

interface AnalyticsSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AnalyticsSettings({ isOpen, onClose }: AnalyticsSettingsProps) {
  const { businessId } = useBusiness();
  const [config, setConfig] = useState<AnalyticsConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'setup' | 'preview'>('setup');

  useEffect(() => {
    if (isOpen && businessId) {
      loadConfig();
    }
  }, [isOpen, businessId]);

  const loadConfig = async () => {
    if (!businessId) return;
    
    setIsLoading(true);
    try {
      const analyticsConfig = await analyticsService.getConfig(businessId);
      setConfig(analyticsConfig);
    } catch (error) {
      console.error('Error loading analytics config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!businessId) return;
    
    setIsSaving(true);
    try {
      const trackingCode = analyticsService.generateTrackingCode(businessId);
      const newConfig = await analyticsService.saveConfig({
        business_id: businessId,
        provider: 'custom',
        is_active: true,
        tracking_code: trackingCode
      });
      
      if (newConfig) {
        setConfig(newConfig);
      }
    } catch (error) {
      console.error('Error activating analytics:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyCode = async () => {
    if (!businessId) return;
    const trackingCode = analyticsService.generateTrackingCode(businessId);

    try {
      await navigator.clipboard.writeText(trackingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying code:', error);
    }
  };

  const handleDeactivate = async () => {
    if (!businessId || !config) return;
    
    setIsSaving(true);
    try {
      const updatedConfig = await analyticsService.saveConfig({
        ...config,
        is_active: false
      });
      
      if (updatedConfig) {
        setConfig(updatedConfig);
      }
    } catch (error) {
      console.error('Error deactivating analytics:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop - Higher z-index than AppSettingsModal */}
      <div 
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100002,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto'
        }}
        onClick={onClose}
      />
      
      {/* Modal - iOS Style - Full screen on mobile */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-[100003] sm:items-center">
        <div className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[90vh] flex flex-col pointer-events-auto overflow-hidden" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          {/* Header - iOS Style */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60 bg-white">
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 flex items-center">
                <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
                Tracking Code
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 hidden sm:block">Get your web analytics tracking code</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Tabs - iOS Style */}
          <div className="flex border-b border-gray-200/60 bg-gray-50/30">
            <button
              onClick={() => setActiveTab('setup')}
              className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
                activeTab === 'setup' 
                  ? 'text-gray-900 bg-white border-b-2 border-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Setup
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
                activeTab === 'preview' 
                  ? 'text-gray-900 bg-white border-b-2 border-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Tracking Code
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 sm:py-16">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm sm:text-base text-gray-600">Loading...</span>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-5">
                {/* Setup Tab */}
                {activeTab === 'setup' && (
                  <div className="space-y-4 sm:space-y-5">
                    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">How it works</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                        Add our tracking code to your website to monitor visitor behavior, page views, 
                        form submissions, and more. The code is lightweight and privacy-focused.
                      </p>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">Analytics Status</h4>
                          <p className="text-xs text-gray-500">
                            {config?.is_active 
                              ? 'Analytics is active and collecting data' 
                              : 'Analytics is not active'
                            }
                          </p>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                          config?.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {config?.is_active ? 'Active' : 'Inactive'}
                        </div>
                      </div>

                      {!config?.is_active ? (
                        <button
                          onClick={handleActivate}
                          disabled={isSaving}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 sm:py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {isSaving ? 'Activating...' : 'Activate Analytics'}
                        </button>
                      ) : (
                        <button
                          onClick={handleDeactivate}
                          disabled={isSaving}
                          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-2.5 sm:py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {isSaving ? 'Deactivating...' : 'Deactivate Analytics'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Tracking Code Tab */}
                {activeTab === 'preview' && (
                  <div className="space-y-4 sm:space-y-5">
                    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">Add to your website</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-3">
                        Copy this tracking code and add it to your website's &lt;head&gt; section. 
                        For Webflow sites, go to Site Settings → Custom Code → Head Code.
                      </p>
                      
                      <a
                        href="https://webflow.com/help/article/add-custom-code-to-your-site"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 text-xs font-medium hover:underline transition-colors"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Webflow Instructions
                      </a>
                    </div>

                    {config?.tracking_code ? (
                      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 text-center">
                        <div className="mb-4 sm:mb-6">
                          <Code className="h-10 w-10 sm:h-12 sm:w-12 text-blue-600 mx-auto mb-3 sm:mb-4" />
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Tracking Code Ready</h4>
                          <p className="text-xs sm:text-sm text-gray-600">
                            Your tracking code has been generated and is ready to be added to your website.
                          </p>
                        </div>
                        
                        <button
                          onClick={handleCopyCode}
                          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg transition-colors font-medium text-sm touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {copied ? (
                            <>
                              <Check className="h-4 w-4" />
                              <span>Copied to Clipboard!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              <span>Copy Tracking Code</span>
                            </>
                          )}
                        </button>
                        
                        <pre className="mt-4 overflow-x-auto rounded-lg bg-gray-50 px-3 py-2 text-left text-[11px] leading-relaxed text-gray-700">
                          {businessId ? analyticsService.generateTrackingCode(businessId) : ''}
                        </pre>
                        <div className="mt-3 text-xs text-gray-500">
                          Paste this where the old snippet is. It loads the current tracker, including route changes, form submissions, and time on the page.
                          Location in the website report uses IP Geolocation by <a href="https://db-ip.com" className="underline" target="_blank" rel="noreferrer">DB-IP</a>.
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 sm:py-12">
                        <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8">
                          <BarChart3 className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                          <p className="text-sm sm:text-base text-gray-600 font-medium">Activate analytics to generate tracking code</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
